/**
 * A shared document, for somebody who has never heard of BroadPaper.
 *
 * The only screen in the application that nobody signs in to, and it is rendered
 * before Clerk is loaded at all — see `main.tsx`. The person here was sent a link
 * by a business; what they want is the document, and anything else on the page is
 * in the way of it. So: who it is from, what it is, the document itself, and a
 * download. One line at the foot says what made it.
 *
 * The document is shown by the browser's own PDF viewer, in a frame, from a link
 * that works for a few minutes. On a phone a framed PDF is a first page and nothing
 * else, so there the frame is replaced by a button that opens the file properly.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, legalUrl, type PublicShare, type SharedDocumentInfo } from "../api.js";
import { Button, Field, Icon, Note, bytes, when } from "../ui/index.js";

type View =
  | { kind: "loading" }
  | { kind: "gone"; message: string; hint?: string | undefined }
  | { kind: "expired"; from: string }
  | { kind: "password"; from: string; error?: string | undefined }
  | { kind: "open"; from: string; document: SharedDocumentInfo; viewUrl: string; fetchedAt: number };

/** How long a view link is trusted before a fresh one is asked for. The service signs them for ten minutes. */
const FRESH_MS = 8 * 60_000;

export function SharedDocument({ token }: { token: string }) {
  const [view, setView] = useState<View>({ kind: "loading" });
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState<"open" | "download" | null>(null);

  const fail = useCallback((e: unknown, from?: string) => {
    if (e instanceof ApiError && e.detail?.needsPassword && from) {
      setView({ kind: "password", from, error: e.code === "rate-limited" ? `${e.message} ${e.hint ?? ""}` : e.message });
    } else if (e instanceof ApiError && e.detail?.state === "expired" && from) {
      setView({ kind: "expired", from });
    } else if (e instanceof ApiError) {
      setView({ kind: "gone", message: e.message, hint: e.hint });
    } else {
      setView({ kind: "gone", message: "This document couldn't be opened.", hint: "Try again in a moment." });
    }
  }, []);

  const open = useCallback(
    async (from: string, withPassword?: string) => {
      setBusy("open");
      try {
        const opened = await api.openShare(token, withPassword);
        if (withPassword) setPassword(withPassword);
        setView({ kind: "open", from, document: opened.document, viewUrl: opened.viewUrl, fetchedAt: Date.now() });
        document.title = `${opened.document.name} · from ${from}`;
      } catch (e) {
        if (e instanceof ApiError && e.detail?.needsPassword) setView({ kind: "password", from, ...(withPassword ? { error: e.message } : {}) });
        else fail(e, from);
      } finally {
        setBusy(null);
      }
    },
    [token, fail]
  );

  useEffect(() => {
    let cancelled = false;
    api
      .publicShare(token)
      .then((share: PublicShare) => {
        if (cancelled) return;
        document.title = `A document from ${share.from}`;
        if (share.state === "expired") setView({ kind: "expired", from: share.from });
        else if (share.needsPassword) setView({ kind: "password", from: share.from });
        // Opened straight away: the person followed a link to a document, and a
        // second click to see it would be a step with nothing in it.
        else void open(share.from);
      })
      .catch((e) => !cancelled && fail(e));
    return () => {
      cancelled = true;
    };
  }, [token, open, fail]);

  const download = async () => {
    if (view.kind !== "open") return;
    setBusy("download");
    try {
      const { url } = await api.downloadShare(token, password);
      window.location.href = url;
    } catch (e) {
      fail(e, view.from);
    } finally {
      setBusy(null);
    }
  };

  /** A fresh link when the one on the page is close to lapsing — somebody may have read for a while. */
  const fullScreen = async () => {
    if (view.kind !== "open") return;
    if (Date.now() - view.fetchedAt < FRESH_MS) {
      window.location.href = view.viewUrl;
      return;
    }
    setBusy("open");
    try {
      const opened = await api.openShare(token, password);
      window.location.href = opened.viewUrl;
    } catch (e) {
      fail(e, view.from);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="shared">
      <header className="shared__bar">
        <img src="/logo.svg" alt="BroadPaper" className="shared__logo" />
        {view.kind === "open" ? (
          <div className="row" style={{ gap: 8 }}>
            <Button size="sm" variant="quiet" icon="external" busy={busy === "open"} onClick={() => void fullScreen()}>
              <span className="shared__wide-only">Open full screen</span>
            </Button>
            <Button size="sm" variant="primary" icon="download" busy={busy === "download"} onClick={() => void download()}>
              Download
            </Button>
          </div>
        ) : null}
      </header>

      {view.kind === "loading" ? (
        <div className="empty">
          <span className="spinner spinner--lg" />
          <p className="muted">Opening the document…</p>
        </div>
      ) : view.kind === "gone" ? (
        <div className="shared__card">
          <span className="empty__mark" />
          <h1>{view.message}</h1>
          {view.hint ? <p className="muted">{view.hint}</p> : null}
        </div>
      ) : view.kind === "expired" ? (
        <div className="shared__card">
          <span className="empty__mark" />
          <span className="eyebrow">From {view.from}</span>
          <h1>This link has expired</h1>
          <p className="muted">Links to documents can be set to stop working after a while. Ask {view.from} for a new one.</p>
        </div>
      ) : view.kind === "password" ? (
        <form
          className="shared__card"
          onSubmit={(e) => {
            e.preventDefault();
            if (typed) void open(view.from, typed);
          }}
        >
          <Icon name="lock" className="shared__lock" />
          <span className="eyebrow">From {view.from}</span>
          <h1>This document needs a password</h1>
          <p className="muted">{view.from} will have given it to you separately from the link.</p>
          <Field label="Password" id="shared-password" error={view.error}>
            <input id="shared-password" className="input input--lg" type="password" autoComplete="current-password" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" size="lg" block busy={busy === "open"} disabled={!typed}>
            Open the document
          </Button>
        </form>
      ) : (
        <main className="shared__doc">
          <div className="shared__title">
            <span className="eyebrow">From {view.from}</span>
            <h1>{view.document.name}</h1>
            <p className="small muted">
              {[
                view.document.pages ? `${view.document.pages} page${view.document.pages === 1 ? "" : "s"}` : null,
                view.document.bytes ? bytes(view.document.bytes) : null,
                `Made ${when(view.document.producedAt)}`
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <iframe className="shared__frame" src={view.viewUrl} title={view.document.name} />
          <div className="shared__narrow-only">
            <Note tone="info" title="Your document is ready">
              Phones show PDFs best in their own viewer.
            </Note>
            <Button variant="primary" size="lg" block icon="external" busy={busy === "open"} onClick={() => void fullScreen()}>
              Open the document
            </Button>
          </div>
        </main>
      )}

      {/*
       * The only thing on this page that is ours rather than the sender's, so it stays
       * one quiet line. Somebody reading a business's invoice is exactly the person who
       * might need documents of their own, and the labels are how the service learns a
       * workspace began here: `utm_source` is handed on by the site, `source` is read by
       * Welcome directly. Neither says anything about the visitor.
       */}
      <footer className="shared__foot">
        Made with <a href="https://www.broadpaper.com/?utm_source=share-page">BroadPaper Cloud</a> — branded documents from your own spreadsheets.{" "}
        <a href="/?source=share-page">Make your own, free</a>
        {" · "}
        {/* A visitor here is counted without being identified; this is where they can read how. */}
        <a href={legalUrl("/legal/privacy/")} target="_blank" rel="noreferrer">
          Privacy
        </a>
      </footer>
    </div>
  );
}
